import { QueryClient } from '@tanstack/react-query';
import { errorMessage } from '@/lib/utils';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: true,
      retry: (failures, error) =>
        failures < 2 && !/permission|not found|jwt|invalid|violates/i.test(errorMessage(error, '')),
    },
    mutations: { retry: 0 },
  },
});

/** Every cache key in one place so invalidation can't drift from reads. */
export const qk = {
  org: (userId: string) => ['org', userId] as const,
  employees: (orgId: string) => ['employees', orgId] as const,
  employeeStats: (id: number) => ['employeeStats', id] as const,
  labels: (orgId: string) => ['labels', orgId] as const,
  overview: (orgId: string, ...args: unknown[]) => ['overview', orgId, ...args] as const,
  reports: (orgId: string, ...args: unknown[]) => ['reports', orgId, ...args] as const,
  calls: (orgId: string, ...args: unknown[]) => ['calls', orgId, ...args] as const,
  callCounts: (orgId: string, ...args: unknown[]) => ['callCounts', orgId, ...args] as const,
  contacts: (orgId: string, ...args: unknown[]) => ['contacts', orgId, ...args] as const,
  contactCounts: (orgId: string, ...args: unknown[]) => ['contactCounts', orgId, ...args] as const,
  contact: (orgId: string, phone: string) => ['contact', orgId, phone] as const,
  contactCalls: (orgId: string, phone: string, ...scope: unknown[]) =>
    ['contactCalls', orgId, phone, ...scope] as const,
  exclusion: (orgId: string, phone: string) => ['exclusion', orgId, phone] as const,
  exclusions: (orgId: string) => ['exclusions', orgId] as const,
  storage: (orgId: string, type: string) => ['storage', orgId, type] as const,
  webhooks: (orgId: string) => ['webhooks', orgId] as const,
  deliveries: (orgId: string) => ['deliveries', orgId] as const,
  apiKey: (orgId: string) => ['apiKey', orgId] as const,
  apiActivity: (orgId: string) => ['apiActivity', orgId] as const,
  connectedApps: (userId: string) => ['connectedApps', userId] as const,
  aiHistory: (orgId: string) => ['aiHistory', orgId] as const,
  aiCall: (callId: number) => ['aiCall', callId] as const,
  tasks: (orgId: string, ...args: unknown[]) => ['tasks', orgId, ...args] as const,
  taskCounts: (orgId: string, ...args: unknown[]) => ['taskCounts', orgId, ...args] as const,
};

/** Anything that changes calls, contacts or their details refreshes every view built from them. */
export function invalidateCallData(orgId: string) {
  const roots = [
    'calls',
    'callCounts',
    'contacts',
    'contactCounts',
    'contact',
    'contactCalls',
    'overview',
    'reports',
    'labels',
    'exclusions',
    'exclusion',
  ];
  return Promise.all(roots.map((root) => queryClient.invalidateQueries({ queryKey: [root, orgId] })));
}

/** A task change refreshes every list and count built from tasks, wherever it was made. */
export function invalidateTasks(orgId: string) {
  return Promise.all(
    ['tasks', 'taskCounts'].map((root) => queryClient.invalidateQueries({ queryKey: [root, orgId] })),
  );
}
